import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";
import Logo from "@/site/Logo";
import { formatApiError } from "@/lib/apiClient";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await register(name, email, password);
      }
      navigate(result?.role === "admin" ? "/espace/admin" : "/mon-espace");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4 font-body relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-emerald-300/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-amber-300/30 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <Logo size={64} />
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight mt-4">FB Environnement</h1>
          <p className="text-stone-500 text-sm mt-1 uppercase tracking-[0.15em] font-bold">Espace commercial</p>
        </div>

        <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-8">
          <h2 className="font-heading font-black text-2xl mb-1" data-testid="auth-title">
            {isLogin ? "Connexion" : "Créer ma session"}
          </h2>
          <p className="text-stone-500 text-sm mb-6">
            {isLogin ? "Entrez vos identifiants pour accéder à votre espace." : "Ouvrez votre session commerciale en quelques secondes."}
          </p>

          {/* Login propre — uniquement email + mot de passe */}

          <form onSubmit={submit} className="space-y-3">
            {!isLogin && (
              <Field
                icon={<UserIcon className="w-5 h-5 text-stone-400" strokeWidth={2.5} />}
                placeholder="Ton prénom"
                value={name}
                onChange={setName}
                testid="register-name"
                required
              />
            )}
            <Field
              icon={<Mail className="w-5 h-5 text-stone-400" strokeWidth={2.5} />}
              type="email"
              placeholder="email@exemple.fr"
              value={email}
              onChange={setEmail}
              testid={isLogin ? "login-email" : "register-email"}
              required
            />
            <Field
              icon={<Lock className="w-5 h-5 text-stone-400" strokeWidth={2.5} />}
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={setPassword}
              testid={isLogin ? "login-password" : "register-password"}
              required
            />

            {error && (
              <div
                data-testid="auth-error"
                className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-2xl px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid={isLogin ? "login-submit" : "register-submit"}
              className="w-full bg-[#1F6B2E] hover:bg-[#175522] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-full transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#1F6B2E]/20"
            >
              {busy ? "Patiente..." : isLogin ? "Se connecter" : "Créer ma session"}
              {!busy && <ArrowRight className="w-4 h-4" strokeWidth={3} />}
            </button>
          </form>

          {!isLogin && (
            <div className="mt-6 text-center text-sm text-stone-500">
              Déjà un compte ?{" "}
              <Link to="/login" data-testid="go-login" className="text-[#1F6B2E] font-bold hover:underline">
                Se connecter
              </Link>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-stone-400 mt-6">
          Accès réservé aux commerciaux FB Environnement
        </p>
      </div>
    </div>
  );
}

function Field({ icon, type = "text", placeholder, value, onChange, testid, required }) {
  return (
    <label className="block">
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2">{icon}</div>
        <input
          data-testid={testid}
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-stone-50 border border-stone-200 focus:bg-white focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 outline-none rounded-2xl pl-12 pr-4 py-3.5 transition-all font-medium"
        />
      </div>
    </label>
  );
}
